import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Popover,
  MenuItem,
  Typography,
  Divider,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import PersonIcon from "@mui/icons-material/Person";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import logo from "../../../assets/Vector.svg";

import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { toggleTheme } from "../../../redux/slices/themeSlice";
import MenuIcon from "@mui/icons-material/Menu";
import SwitchInput from "../../atoms/SwitchInput";
import { useNavigate } from "react-router-dom";

interface NavbarProps {
  onDrawerToggle?: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ onDrawerToggle }) => {
  const dispatch = useDispatch();
  const mode = useSelector((state: RootState) => state.theme.mode);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleSettingsClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleProfileClick = () => {
    // Handle profile navigation
    navigate("/profile");
    handleClose();
  };

 

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: theme.palette.background.paper,
          color: "text.primary",
          width: "100%",
          maxWidth: "100%",
          zIndex: theme.zIndex.drawer + 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
        elevation={0}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            minHeight: { xs: 64 },
            gap: isMobile ? 0 : 2,
            position: "relative",
            width: "100%",
            maxWidth: "100%",
            px: { xs: 1, sm: 2, md: 3 },
            py: { xs: 0.5, sm: 1 },
          }}
        >
          {/* Left Section */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 11},
              minWidth: 0,
            }}
          >
            {/* Menu Button - Always visible on mobile */}
            

            {/* Logo - Hidden on very small screens */}
            {!isMobile && (
              <Box
                component="img"
                src={logo}
                alt="Woopsa"
                sx={{
                  height: { xs: 24, sm: 28 },
                  display: { xs: "none", sm: "block" },
                }}
              />
            )}
            <IconButton
              color="primary"
              edge="start"
              onClick={onDrawerToggle}
              sx={{
                border: "1px solid",
                borderColor: theme.palette.divider,
                borderRadius: "8px",
                padding: 0.5,
                height: { xs: 32},
                width: { xs: 32 },
                marginLeft: { xs: 0 },
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
              aria-label="Toggle navigation menu"
            >
              <MenuIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
            </IconButton>
          </Box>

          {/* Center Section - Empty for now */}
          <Box sx={{ flex: 1 }} />

          {/* Right Section */}
          <Box
            display="flex"
            alignItems="center"
            gap={{ xs: 0.5, sm: 1 }}
            sx={{
              ml: "auto",
            }}
          >
            {/* Notifications */}
            <IconButton 
              color="primary"
              sx={{
                p: { xs: 0.75, sm: 1 },
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
              aria-label="Notifications"
            >
              <NotificationsIcon sx={{ fontSize: { xs: 20, sm: 22 } }} />
            </IconButton>

            {/* User Menu */}
            <IconButton 
              onClick={handleSettingsClick}
              sx={{ 
                color: "primary.main",
                p: { xs: 0.75, sm: 1 },
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
              aria-label="User menu"
            >
              <AccountCircleIcon sx={{ fontSize: { xs: 20, sm: 22 } }} />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* User Menu Popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 200,
            boxShadow: theme.shadows[8],
            border: `1px solid ${theme.palette.divider}`,
          },
        }}
      >
        <Box sx={{ p: 1 }}>
          <MenuItem onClick={handleProfileClick} sx={{ borderRadius: 1 }}>
            <PersonIcon sx={{ mr: 2, fontSize: 20 }} />
            <Typography variant="body2">Profile</Typography>
          </MenuItem>
      
          <Divider sx={{ my: 1 }} />
                     <MenuItem sx={{ borderRadius: 1 }}>
             <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
               <Typography variant="body2" sx={{ mr: 2 }}>
                 {mode === "dark" ? "Light" : "Dark"} Mode
               </Typography>
               <SwitchInput
                 checked={mode === "dark"}
                 onChange={() => dispatch(toggleTheme())}
                 isShowLabel={false}
                 sx={{mb:0}}
               />
             </Box>
           </MenuItem>
        </Box>
      </Popover>
    </>
  );
};

export default Navbar;
